<script setup lang="ts">
  import type { GenerateEmailBodyData } from '@api/resources/emails';
  import { toTypedSchema } from '@vee-validate/zod';
  import { useDebounceFn } from '@vueuse/core';
  import * as changeCase from 'change-case';
  import { storeToRefs } from 'pinia';
  import pluralize from 'pluralize';
  import { useForm } from 'vee-validate';
  import type { HTMLAttributes } from 'vue';
  import { computed, nextTick, onBeforeMount, ref, shallowRef } from 'vue';
  import { useRouter } from 'vue-router';
  import * as zod from 'zod';

  import type {
    Attachment,
    Email,
    EmailSender,
    EmailThread,
    Partner,
    Task,
  } from '@stuut/api/models';
  import { HttpError } from '@stuut/common/src/api_client';
  import { formatFileSize } from '@stuut/common/src/utils';
  import { Alert } from '@ui/components/alert';
  import { Badge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { ConfirmDialog } from '@ui/components/confirm-dialog';
  import { FormControl, FormField, FormItem } from '@ui/components/form';
  import { HoverCard, HoverCardTrigger } from '@ui/components/hover-card';
  import type { SaveStatus } from '@ui/components/save-status';
  import { Separator } from '@ui/components/separator';
  import { toast } from '@ui/components/sonner';
  import { TextEditor, TextEditorToolbar } from '@ui/components/text-editor';
  import type { Editor } from '@ui/components/text-editor';

  import { stuut } from '@/api';
  import extractingGif from '@/assets/lil-stuut/extracting.gif';
  import { AttachmentPresignItem } from '@/components/attachments';
  import AttachmentItem from '@/components/attachments/AttachmentItem.vue';
  import AttachmentBar from '@/components/emails/AttachmentBar.vue';
  import EmailAttachment from '@/components/emails/EmailAttachment.vue';
  import EmailBodyGenerateMenu from '@/components/emails/EmailBodyGenerateMenu.vue';
  import EmailRecipientFields from '@/components/emails/EmailRecipientFields.vue';
  import EmailSignature from '@/components/emails/EmailSignature.vue';
  import TaskResponsePreview from '@/components/emails/TaskResponsePreview.vue';
  import { detectLanguageAndDirection } from '@/components/emails/utils';
  import {
    FileDropzoneContent,
    FileDropzoneOverlay,
    FileDropzoneProvider,
  } from '@/components/file-upload-dropzone';
  import { useCreditMemoAttachments } from '@/composables/useCreditMemoAttachments';
  import { useInvoiceAttachments } from '@/composables/useInvoiceAttachments';
  import { MAX_TOTAL_ATTACHMENT_SIZE } from '@/constants/Attachments';
  import { useEmailSenders, useTaskCollectionStore, useUsers } from '@/store';
  import { useFeatureFlags } from '@/store/featureFlags';

  interface Props {
    contentClass?: HTMLAttributes['class'];
    email: Email;
    showButtons?: boolean;
    showGenerationOptions?: boolean;
    showRelatedAttachments?: boolean;
    showReplyTo?: boolean;
    showToolbar?: boolean;
    thread: EmailThread;
  }

  const props = withDefaults(defineProps<Props>(), {
    showButtons: true,
    showGenerationOptions: true,
    showRelatedAttachments: true,
    showReplyTo: true,
    showToolbar: true,
  });

  const emits = defineEmits<{
    (e: 'deleted'): void;
    (e: 'updated'): void;
  }>();

  const router = useRouter();

  const editor = shallowRef<Editor | null>(null);

  const saveStatus = ref<SaveStatus>('saved');

  const emailToolbarId = ref<string>(`email-toolbar-${props.email.id}`);

  const MIN_GENERATING_MS = 1500;
  const isGenerating = ref(false);
  const isDeleting = ref(false);
  const isSubmitting = ref(false);

  const currentAttachmentsSize = computed<number>(() => props.email?.attachmentsTotalSize ?? 0);

  const isAttachmentBudgetFull = computed<boolean>(
    () => currentAttachmentsSize.value >= MAX_TOTAL_ATTACHMENT_SIZE,
  );

  const emailSenderStore = useEmailSenders();
  const { emailSenders } = storeToRefs(emailSenderStore);

  const parentEmail = computed<Email | null>(() => {
    if (props.email.parent_email_id) {
      return props.thread.getEmailById(props.email.parent_email_id);
    }

    return null;
  });

  const isReplyDraft = computed(
    () => !!props.email.parent_email_id && !props.email.forwarded_from_email_id,
  );

  const featureFlagsStore = useFeatureFlags();
  const taskHitlEnabled = computed(() => featureFlagsStore.flags['task-hitl']);

  const parentResponseTasks = computed(() =>
    isReplyDraft.value
      ? parentEmail.value?.tasks?.filter((t) => t.isOpen && t.isReplyRequired)
      : undefined,
  );

  const suggestedResponseTask = ref<Task>();

  const isBodyEmpty = computed(() => {
    const body = formData.values.body || '';
    return !new DOMParser().parseFromString(body, 'text/html').body.textContent?.trim();
  });

  const showSuggestedResponse = computed(
    () =>
      taskHitlEnabled.value &&
      isBodyEmpty.value &&
      suggestedResponseTask.value?.responseData?.data?.html_body,
  );

  const useSuggestedResponse = () => {
    const html = suggestedResponseTask.value?.responseData?.data?.html_body;
    if (!html) return;

    updateField('body', html);
    if (editor.value) {
      editor.value.commands.setContent(html);
    }
  };

  const taskCollectionStore = useTaskCollectionStore();

  const openSuggestedTask = () => {
    if (!suggestedResponseTask.value) return;
    nextTick(() =>
      setTimeout(() => {
        taskCollectionStore.openTaskByQuery?.(suggestedResponseTask.value!);
      }, 0),
    );
  };

  const partner = ref<Partner>();
  const userStore = useUsers();

  const users = computed(() => userStore.users);

  const selectedEmailSender = ref<EmailSender | null>(null);

  const creditMemoAttachments = useCreditMemoAttachments({
    disabled: () => isSubmitting.value,
    entity: () => props.email,
    partnerId: () => partner.value?.id,
    resource: stuut.emails,
  });

  const invoiceAttachments = useInvoiceAttachments({
    disabled: () => isSubmitting.value,
    entity: () => props.email,
    partnerId: () => partner.value?.id,
    relatedInvoices: () => props.email.thread?.invoices ?? [],
    resource: stuut.emails,
  });

  const bodyTextDir = computed(() => detectLanguageAndDirection(formData.values.body).direction);

  onBeforeMount(async () => {
    if (props.thread.partner) {
      partner.value = await stuut.partners.retrieve(props.thread.partner.id);

      await userStore.fetchUsers();
    }

    const sender = props.email.email_sender;
    selectedEmailSender.value = props.email.email_sender;
    if (sender) {
      if (sender.is_nylas_grant_expired) {
        toast.error(`${sender.email} has expired`, {
          action: {
            label: 'Settings',
            onClick: () =>
              router.push({
                name: 'settings.connectors.connected-email',
              }),
          },
          description: 'Relink it in your Connector settings',
        });
      }
    } else if (emailSenders.value.length === 0) {
      toast.error('No email senders found', {
        action: {
          label: 'Settings',
          onClick: () =>
            router.push({
              name: 'settings.connectors.connected-email',
            }),
        },
        description: `Link an email in your Connector settings`,
      });
    }

    // eslint-disable-next-line vue/no-mutating-props
    if (!props.email.thread?.id) props.email.thread = props.thread;

    if (parentResponseTasks.value?.length) {
      const fullTask = await stuut.tasks.retrieve(parentResponseTasks.value[0].id);
      if (fullTask.responseData?.data?.html_body) {
        suggestedResponseTask.value = fullTask;
      }
    }
  });

  const onEditorReady = (e: Editor) => {
    editor.value = e;
  };

  const getFormInitialState = () => {
    return {
      bcc: props.email.bcc,
      body: props.email.body ?? '',
      cc: props.email.cc,
      email_sender_id: props.email.email_sender_id || props.email.email_sender?.id,
      reply_to: props.email.reply_to,
      subject: props.email.subject ?? '',
      to: props.email.to,
    };
  };

  const baseFormSchema = zod.object({
    bcc: zod.array(zod.string().email()),
    body: zod.string(),
    cc: zod.array(zod.string().email()),
    email_sender_id: zod.string(),
    reply_to: zod.array(zod.string().email()),
    subject: zod.string(),
    to: zod.array(zod.string().email()),
  });

  const sendSchema = baseFormSchema.extend({
    body: zod.string().min(1),
    email_sender_id: zod.string().min(1),
    subject: zod.string().min(1),
    to: zod.array(zod.string().email()).min(1),
  });

  const formData = useForm({
    initialValues: getFormInitialState(),
    validationSchema: toTypedSchema(sendSchema),
  });

  const canSubmit = computed(() => {
    return (
      formData.values.subject &&
      formData.values.to?.length &&
      formData.values.body?.trim() &&
      formData.values.email_sender_id
    );
  });

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (props.email.hasUploadingAttachments || isSubmitting.value || isGenerating.value) return;

    try {
      await save();
    } catch (error) {
      toast.error('Failed to send. Please try again.');
      return;
    }

    return formData.validate().then((result) => {
      if (!result.valid) {
        const invalidFields = Object.keys(result.results)
          .filter((key) => result.results[key].valid === false)
          .map((field) => changeCase.capitalCase(field).split(' ')[0]);

        toast.error(
          `Could not send email. Fix these fields and retry: ${invalidFields.join(', ')}`,
        );

        return;
      }

      isSubmitting.value = true;

      return stuut.emails
        .send(props.email.id, props.email)
        .then(() => {
          emits('updated');
        })
        .catch((err) => {
          toast.error(err.clientMessage);
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    });
  };

  const deleteEmail = () => {
    if (isSubmitting.value || isDeleting.value) return;

    isDeleting.value = true;

    return stuut.emails
      .delete(props.email.id, { data: {} })
      .then(() => {
        toast.success('Email has been deleted');
        emits('deleted');
      })
      .catch((err) => {
        toast.error(err.clientMessage);
      })
      .finally(() => {
        isDeleting.value = false;
      });
  };

  const save = async () => {
    if (isGenerating.value) return;
    const updateForm = useForm({
      initialValues: formData.values,
      validationSchema: toTypedSchema(baseFormSchema),
    });
    const data: Record<string, any> = { ...formData.values };
    Object.entries(formData.values).forEach(([key, value]) => {
      // @ts-ignore
      if (formData.isFieldDirty(key)) {
        data[key] = formData.values[key];
      }
    });
    if (!Object.keys(data).length) return;

    // @ts-ignore
    const initialState = formData.initialValues?.value || {};
    formData.resetForm({ values: { ...initialState, ...data } });
    return updateForm.validate().then((result) => {
      saveStatus.value = 'saving';

      if (data.email_sender_id) {
        data.email_sender = data.email_sender_id;
        delete data.email_sender_id;
      }

      return stuut.emails
        .update(props.email.id, { data }, props.email)
        .then(() => {
          selectedEmailSender.value = props.email.email_sender;
          saveStatus.value = 'saved';
          emits('updated');
        })
        .catch((error) => {
          saveStatus.value = 'dirty';
          throw error;
        });
    });
  };

  const debouncedUpdate = useDebounceFn(() => {
    save();
  }, 750);

  const handleNewFileAttachments = (newAttachments: Attachment[]) => {
    props.email.addAttachments(newAttachments);
  };

  const handleFailedAttachment = (attachment: Attachment) => {
    props.email.removeAttachment(attachment);
  };

  const generateBody = async (options: GenerateEmailBodyData) => {
    isGenerating.value = true;
    const startedAt = Date.now();

    try {
      const eml = await stuut.emails.generateBody(props.email.id, options);

      props.email.updateFromJson(eml.toJson());

      if (editor.value) {
        editor.value.commands.setContent(eml.body);
      }

      formData.resetForm({ values: getFormInitialState() });
    } catch (err) {
      const message = err instanceof HttpError ? err.clientMessage : 'Failed to generate email';
      toast.error(message || 'Failed to generate email');
      console.error(err);
    } finally {
      const remaining = Math.max(0, MIN_GENERATING_MS - (Date.now() - startedAt));
      setTimeout(() => {
        isGenerating.value = false;
      }, remaining);
    }
  };

  const updateField = (
    name: 'body' | 'to' | 'cc' | 'bcc' | 'subject' | 'email_sender_id',
    value: any,
  ) => {
    formData.setFieldValue(name, value);
    debouncedUpdate();
  };

  const scrollToAttachments = () => {
    const el = document.getElementById(`${props.email.id}-attachments`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
</script>

<template>
  <FileDropzoneProvider
    v-slot="{ openFileInput }"
    :max-total-size="MAX_TOTAL_ATTACHMENT_SIZE"
    :current-attachments-size="currentAttachmentsSize"
    @on-new-file-attachments="handleNewFileAttachments"
  >
    <FileDropzoneContent
      class="relative rounded min-w-85 overflow-hidden flex flex-col"
      :class="contentClass"
    >
      <form
        class="flex flex-col grow"
        as="div"
        @submit="onSubmit"
        @keydown.enter="(e) => e.preventDefault()"
      >
        <EmailRecipientFields
          class="p-5"
          :email-sender="selectedEmailSender"
          :parent-email="parentEmail"
          :to="formData.values.to ?? []"
          :cc="formData.values.cc ?? []"
          :bcc="formData.values.bcc ?? []"
          :subject="formData.values.subject ?? ''"
          :partner="partner"
          :users="users"
          :hide-subject="isReplyDraft"
          :hide-to="!showReplyTo"
          :suggest-contacts="true"
          :disabled="isGenerating || isSubmitting"
          @update:email-sender="(es) => updateField('email_sender_id', es?.id)"
          @update:to="(v) => updateField('to', v)"
          @update:cc="(v) => updateField('cc', v)"
          @update:bcc="(v) => updateField('bcc', v)"
          @update:subject="(v) => updateField('subject', v)"
        />

        <div
          v-if="(editor && showToolbar) || showGenerationOptions"
          class="flex items-center gap-2 w-full border-y py-2 px-3.5"
        >
          <TextEditorToolbar v-if="editor && showToolbar" :editor="editor as Editor" />

          <Separator
            v-if="showGenerationOptions && editor && showToolbar"
            class="h-5 my-auto mx-0"
            orientation="vertical"
          />

          <EmailBodyGenerateMenu
            v-if="showGenerationOptions"
            :is-generating="isGenerating"
            @generate-body="generateBody"
          />
        </div>

        <div class="relative flex flex-col">
          <div class="flex flex-col overflow-y-auto max-h-80">
            <div class="flex flex-col px-6 py-5">
              <HoverCard :open-delay="200">
                <HoverCardTrigger as-child>
                  <Alert
                    v-if="showSuggestedResponse"
                    variant="secondary"
                    size="xs"
                    icon="fas fa-sparkles text-agent"
                    class="mb-4 py-1.5 px-2"
                  >
                    <div class="flex items-center justify-between w-full">
                      <div class="flex items-center gap-x-1.5 text-xs">
                        <span>A task provided a suggested response for this email.</span>

                        <Button
                          variant="link"
                          size="xs"
                          class="h-auto p-0 text-xs"
                          @click.stop.prevent="openSuggestedTask"
                          >View Task</Button
                        >
                      </div>

                      <Button size="xs" variant="agent" @click="useSuggestedResponse">
                        Start using suggested
                      </Button>
                    </div>
                  </Alert>
                </HoverCardTrigger>

                <TaskResponsePreview v-if="suggestedResponseTask" :task="suggestedResponseTask" />
              </HoverCard>

              <div
                v-if="isGenerating"
                class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/60"
              >
                <img :src="extractingGif" alt="Generating" class="size-35" />
              </div>

              <div class="flex-1 w-full overflow-y-auto min-h-65 relative">
                <FormField name="body">
                  <FormItem v-auto-animate>
                    <FormControl>
                      <TextEditor
                        :dir="bodyTextDir"
                        :model-value="formData.values.body"
                        :toolbar="`#${emailToolbarId}`"
                        placeholder-text="Type your message here"
                        class="h-full flex-1"
                        :disable-editing="isSubmitting"
                        :disabled="isGenerating || isSubmitting"
                        @update:model-value="(event) => updateField('body', event)"
                        @ready="onEditorReady"
                      />

                      <p
                        v-if="email.error_message"
                        class="text-destructive text-xs pt-2.5"
                        aria-live="polite"
                      >
                        {{ email.error_message }}
                      </p>
                    </FormControl>
                  </FormItem>
                </FormField>

                <EmailSignature
                  class="pb-5"
                  :email-from="selectedEmailSender?.email"
                  :created-at="email.created_at"
                />

                <div
                  v-if="
                    email.attachments?.length ||
                    (showRelatedAttachments &&
                      (props.email?.attachableInvoices?.length ||
                        creditMemoAttachments.attachableCreditMemos.value.length))
                  "
                >
                  <div class="space-y-4">
                    <div
                      v-if="email.attachments?.length"
                      :id="`${email.id}-attachments`"
                      class="flex flex-col gap-4"
                    >
                      <div class="flex items-center justify-between">
                        <h3 class="font-semibold text-xs text-muted-foreground">Attached</h3>
                        <span
                          class="text-xs text-muted-foreground"
                          :class="isAttachmentBudgetFull && 'text-destructive'"
                        >
                          {{ formatFileSize(currentAttachmentsSize) }} of
                          {{ formatFileSize(MAX_TOTAL_ATTACHMENT_SIZE) }}
                        </span>
                      </div>

                      <div role="list" class="flex flex-wrap gap-2.5 items-center">
                        <AttachmentPresignItem
                          v-for="(attachment, index) in email.attachments"
                          :key="index"
                          role="listitem"
                          :attachment="attachment"
                          :entity-model="email"
                          @failed="handleFailedAttachment"
                        >
                          <EmailAttachment :attachment="attachment" :email="email" />
                        </AttachmentPresignItem>
                      </div>
                    </div>

                    <div
                      v-if="
                        showRelatedAttachments &&
                        (props.email?.attachableInvoices?.length ||
                          creditMemoAttachments.attachableCreditMemos.value.length)
                      "
                      class="flex flex-col gap-4"
                    >
                      <h3 class="font-semibold text-xs text-muted-foreground">Suggested</h3>

                      <div role="list" class="flex flex-wrap gap-2.5 items-center">
                        <AttachmentItem
                          v-for="invoice in props.email?.attachableInvoices"
                          :key="`inv-${invoice.id}`"
                          role="listitem"
                          :dashed="true"
                          :show-left-icon="false"
                          :show-right-icon="true"
                          :right-icon-hover="true"
                          :uploading="invoiceAttachments.isInvoicePending(invoice)"
                          right-icon="fa-plus-circle"
                          @right-icon-click="invoiceAttachments.toggleInvoiceAttachment(invoice)"
                        >
                          <template #preview_trigger>
                            <span>{{ invoice.number }}</span>
                          </template>

                          <template v-if="invoice.pdf" #preview_content>
                            <img :src="invoice.pdf" :alt="invoice.number || ''" />
                          </template>
                        </AttachmentItem>

                        <AttachmentItem
                          v-for="cm in creditMemoAttachments.attachableCreditMemos.value"
                          :key="`cm-${cm.id}`"
                          role="listitem"
                          :dashed="true"
                          :show-left-icon="false"
                          :show-right-icon="true"
                          :right-icon-hover="true"
                          :uploading="creditMemoAttachments.isCreditMemoPending(cm)"
                          right-icon="fa-plus-circle"
                          @right-icon-click="creditMemoAttachments.toggleAttachCreditMemo(cm)"
                        >
                          <template #preview_trigger>
                            <span>{{ cm.number }}</span>
                          </template>

                          <template v-if="cm.pdf" #preview_content>
                            <img :src="cm.pdf" :alt="cm.number || ''" />
                          </template>
                        </AttachmentItem>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="showButtons"
            class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-2 px-4 py-3 border-t bg-muted/30"
          >
            <div class="flex items-center gap-x-2">
              <AttachmentBar
                class="justify-between"
                :invoice-attachments="invoiceAttachments"
                :credit-memo-attachments="creditMemoAttachments"
                :related-invoices="email.thread?.invoices ?? []"
                :credit-memo-enabled="true"
                :open-file-input="openFileInput"
                :disabled="isSubmitting"
                :is-attachment-budget-full="isAttachmentBudgetFull"
              />

              <template v-if="email.attachments?.length">
                <Separator class="h-5 my-auto mx-0" orientation="vertical" />

                <Badge
                  class="cursor-pointer ml-2.5"
                  variant="secondary"
                  @click="scrollToAttachments"
                >
                  <i class="fas fa-paperclip text-2xs" />
                  <span class="hidden xl:block">{{
                    pluralize('attachments', email.attachments?.length, true)
                  }}</span>

                  <span class="xl:hidden">{{ email.attachments?.length }}</span>
                </Badge>
              </template>
            </div>

            <div class="flex items-center gap-x-2">
              <ConfirmDialog confirm-text="Delete" @confirm="deleteEmail">
                <template #title> Are you sure?</template>

                <template #description>
                  This will permanently delete this email. This action cannot be undone.
                </template>

                <template #trigger>
                  <Button
                    variant="ghost"
                    type="button"
                    size="sm"
                    :loading="isDeleting"
                    :disabled="isSubmitting || isDeleting"
                    class="text-muted-foreground hover:text-destructive"
                  >
                    <span>Discard</span>
                  </Button>
                </template>
              </ConfirmDialog>

              <Button
                type="submit"
                size="sm"
                :loading="saveStatus === 'saving' || isSubmitting"
                :disabled="
                  isDeleting || isGenerating || !canSubmit || email.hasUploadingAttachments
                "
                class="gap-x-1.5"
              >
                <i class="fa-solid fa-paper-plane text-2xs" />
                <span>Send Email</span>
              </Button>
            </div>
          </div>
        </div>
      </form>

      <FileDropzoneOverlay />
    </FileDropzoneContent>
  </FileDropzoneProvider>
</template>

<style lang="postcss"></style>
